import {
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Plus,
  Save,
  Trash2,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Input } from '@/components/ui/input'
import { AUTO_SAVE_DEBOUNCE_MS, useAutoSave } from '@/hooks/use-auto-save'
import { trpc } from '@/lib/trpc'
import type {
  ClientFormData,
  CoachingClient,
  NotionTemplate,
} from '../problems/types'
import { HOMEWORK_TYPE_OPTIONS } from '../problems/types'
import { Badge } from '../shared/badge'

/**
 * Validates if a string is a valid Notion database ID (32 hex chars, with or without hyphens)
 */
function isValidNotionDatabaseId(id: string): boolean {
  if (!id) return false
  // Remove hyphens and check if it's 32 hex characters
  const cleanId = id.replace(/-/g, '')
  return /^[a-f0-9]{32}$/i.test(cleanId)
}

/**
 * Extracts a Notion database ID from a URL or returns the ID if already valid
 * Handles formats like:
 * - https://www.notion.so/abc123def456...?v=xyz
 * - https://www.notion.so/workspace/abc123def456...?v=xyz
 * - abc123def456... (raw ID)
 * - abc123-def4-5678-... (ID with hyphens)
 */
function extractNotionDatabaseId(input: string): string {
  if (!input) return ''

  // If it's already a valid ID (with or without hyphens), return it cleaned
  const cleanInput = input.trim().replace(/-/g, '')
  if (/^[a-f0-9]{32}$/i.test(cleanInput)) {
    return cleanInput
  }

  // Try to extract from URL
  try {
    // Match the 32-character hex ID pattern in URLs
    // It appears after the last slash and before any query params
    const urlPattern =
      /([a-f0-9]{32}|[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i
    const match = input.match(urlPattern)
    if (match) {
      return match[1].replace(/-/g, '')
    }
  } catch {
    // Not a valid URL, return original input
  }

  return input.trim()
}

interface FieldConfig {
  key: keyof ClientFormData
  label: string
  type: 'text' | 'email' | 'tel' | 'url' | 'select' | 'textarea'
  options?: string[]
}

const BASIC_INFO_FIELDS: FieldConfig[] = [
  { key: 'namePronunciation', label: 'Name Pronunciation', type: 'text' },
  {
    key: 'programmingLanguage',
    label: 'Programming Language',
    type: 'select',
    options: [
      'Python',
      'Java',
      'JavaScript',
      'TypeScript',
      'C++',
      'Go',
      'Rust',
      'C#',
    ],
  },
]

const CONTACT_INFO_FIELDS: FieldConfig[] = [
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'phone', label: 'Phone', type: 'tel' },
  { key: 'address', label: 'Address', type: 'text' },
  { key: 'location', label: 'Location', type: 'text' },
  { key: 'timeZone', label: 'Time Zone', type: 'text' },
]

const PROFILES_FIELDS: FieldConfig[] = [
  { key: 'discordUsername', label: 'Discord Username', type: 'text' },
  { key: 'linkedinProfile', label: 'LinkedIn Profile', type: 'url' },
  { key: 'githubProfile', label: 'GitHub Profile', type: 'url' },
  { key: 'leetcodeProfile', label: 'LeetCode Profile', type: 'url' },
  {
    key: 'interviewingIoUsername',
    label: 'Interviewing.io Username',
    type: 'text',
  },
]

const ADDITIONAL_INFO_FIELDS: FieldConfig[] = [
  { key: 'targetRoles', label: 'Target Roles', type: 'text' },
]

// Note: notionDatabaseId is handled separately with custom validation

function SectionDivider({ title }: { title: string }) {
  return (
    <div className="border-border border-t pt-4 pb-2">
      <h3 className="text-foreground text-sm font-semibold tracking-wider uppercase">
        {title}
      </h3>
    </div>
  )
}

interface PersonalDetailsProps {
  client: CoachingClient
  onUpdate: (data: Partial<ClientFormData>) => Promise<unknown>
}

export function PersonalDetails({ client, onUpdate }: PersonalDetailsProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [formData, setFormData] = useState<ClientFormData>({ name: '' })

  const {
    state: saveState,
    hasUnsavedChanges: hasChanges,
    onChange: scheduleSave,
    cancel: cancelSave,
  } = useAutoSave({
    value: formData,
    onSave: onUpdate,
    debounceMs: AUTO_SAVE_DEBOUNCE_MS.medium,
    logLabel: 'Personal details',
  })
  const saving = saveState === 'saving'

  // Re-seed the form only when we switch to a different client. Keying on the
  // stable `client.id` (rather than the whole `client` object) stops a
  // background refetch of the same client — triggered by sibling widgets on
  // this page (homework assign/complete → getById.invalidate) — from
  // clobbering the coach's in-progress, unsaved edits.
  // biome-ignore lint/correctness/useExhaustiveDependencies: client.id triggers a re-seed when the client identity changes, not on every refetch of the same client
  useEffect(() => {
    if (client) {
      const allFields = [
        ...BASIC_INFO_FIELDS,
        ...CONTACT_INFO_FIELDS,
        ...PROFILES_FIELDS,
        ...ADDITIONAL_INFO_FIELDS,
      ]
      setFormData({
        name: client.name || '',
        status: client.status?.toLowerCase() || 'active',
        // Include fields that are rendered manually (not in field arrays)
        availabilityNotes: client.availabilityNotes || '',
        dropboxRecordingsLink: client.dropboxRecordingsLink || '',
        notionDatabaseId: client.notionDatabaseId || '',
        notionTemplates: client.notionTemplates || [],
        ...allFields.reduce(
          (acc, field) => {
            acc[field.key] =
              (client[field.key as keyof CoachingClient] as string) || ''
            return acc
          },
          {} as Record<string, string>,
        ),
      } as ClientFormData)
      // The freshly seeded form is the server's copy: drop any queued save from
      // the client we just navigated away from.
      cancelSave()
    }
  }, [client.id, cancelSave])

  const sourcesQuery = trpc.admin.sources.list.useQuery()

  const handleChange = (key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }))
    // No argument: the save picks up the whole form as of the debounce firing.
    scheduleSave()
  }

  // Template management handlers
  const handleAddTemplate = useCallback(() => {
    const newTemplate: NotionTemplate = {
      name: '',
      templateId: '',
      source: null,
      homeworkType: null,
    }
    setFormData((prev) => ({
      ...prev,
      notionTemplates: [...(prev.notionTemplates || []), newTemplate],
    }))
    scheduleSave()
  }, [scheduleSave])

  const handleUpdateTemplate = useCallback(
    (index: number, field: keyof NotionTemplate, value: string | null) => {
      setFormData((prev) => {
        const templates = [...(prev.notionTemplates || [])]
        templates[index] = { ...templates[index], [field]: value }
        return { ...prev, notionTemplates: templates }
      })
      scheduleSave()
    },
    [scheduleSave],
  )

  const handleRemoveTemplate = useCallback(
    (index: number) => {
      setFormData((prev) => {
        const templates = [...(prev.notionTemplates || [])]
        templates.splice(index, 1)
        return { ...prev, notionTemplates: templates }
      })
      scheduleSave()
    },
    [scheduleSave],
  )

  const renderField = (field: FieldConfig) => {
    const value = (formData[field.key] as string) || ''

    if (field.type === 'url') {
      return (
        <div className="flex items-center gap-2">
          <Input
            type="url"
            value={value}
            onChange={(e) => handleChange(field.key, e.target.value)}
            placeholder={`Enter ${field.label.toLowerCase()}`}
            className="flex-1"
          />
          {value && (
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:bg-muted rounded p-2"
            >
              <ExternalLink size={16} />
            </a>
          )}
        </div>
      )
    }

    if (field.type === 'select' && field.options) {
      return (
        <select
          value={value}
          onChange={(e) => handleChange(field.key, e.target.value)}
          className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
        >
          <option value="">Select {field.label.toLowerCase()}</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      )
    }

    if (field.type === 'textarea') {
      return (
        <textarea
          value={value}
          onChange={(e) => handleChange(field.key, e.target.value)}
          className="border-input bg-background min-h-[80px] w-full resize-y rounded-md border px-3 py-2 text-sm"
          placeholder={`Enter ${field.label.toLowerCase()}`}
        />
      )
    }

    return (
      <Input
        type={field.type}
        value={value}
        onChange={(e) => handleChange(field.key, e.target.value)}
        placeholder={`Enter ${field.label.toLowerCase()}`}
      />
    )
  }

  const renderFields = (fields: FieldConfig[]) => {
    return fields.map((field) => (
      <div
        key={field.key}
        className={field.type === 'textarea' ? 'md:col-span-3' : ''}
      >
        <label className="text-muted-foreground mb-1 block text-sm font-medium">
          {field.label}
        </label>
        {renderField(field)}
      </div>
    ))
  }

  return (
    <div className="bg-card border-border overflow-hidden rounded-lg border">
      {/* Header - Always visible */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="hover:bg-muted/50 flex w-full items-center justify-between p-4 transition-colors"
      >
        <div className="flex items-center gap-4">
          <h2 className="text-foreground text-lg font-semibold">
            {formData.name || 'Personal Details'}
          </h2>
          <Badge variant={formData.status}>{formData.status}</Badge>
          {hasChanges && (
            <span className="text-primary flex items-center gap-1 text-xs">
              <Save size={12} />
              {saving ? 'Saving...' : 'Unsaved changes'}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp size={20} className="text-muted-foreground" />
        ) : (
          <ChevronDown size={20} className="text-muted-foreground" />
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-border border-t p-4 pt-0">
          <div className="space-y-6 pt-4">
            {/* Name, Status, and Dropbox Recordings */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="text-muted-foreground mb-1 block text-sm font-medium">
                  Name
                </label>
                <Input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Client name"
                />
              </div>
              <div>
                <label className="text-muted-foreground mb-1 block text-sm font-medium">
                  Status
                </label>
                <select
                  value={formData.status || 'active'}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
                >
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div>
                <label className="text-muted-foreground mb-1 block text-sm font-medium">
                  Dropbox Recordings
                </label>
                {renderField({
                  key: 'dropboxRecordingsLink',
                  label: 'Dropbox Recordings',
                  type: 'url',
                })}
              </div>
            </div>

            {/* Basic Info Section */}
            <div>
              <SectionDivider title="Basic Info" />
              <div className="grid grid-cols-1 gap-4 pt-2 md:grid-cols-3">
                {renderFields(BASIC_INFO_FIELDS)}
              </div>
            </div>

            {/* Contact Info Section */}
            <div>
              <SectionDivider title="Contact Info" />
              <div className="grid auto-rows-min grid-cols-1 gap-4 pt-2 md:grid-cols-4">
                <div>
                  <label className="text-muted-foreground mb-1 block text-sm font-medium">
                    Email
                  </label>
                  {renderField({ key: 'email', label: 'Email', type: 'email' })}
                </div>
                <div>
                  <label className="text-muted-foreground mb-1 block text-sm font-medium">
                    Phone
                  </label>
                  {renderField({ key: 'phone', label: 'Phone', type: 'tel' })}
                </div>
                <div>
                  <label className="text-muted-foreground mb-1 block text-sm font-medium">
                    Address
                  </label>
                  {renderField({
                    key: 'address',
                    label: 'Address',
                    type: 'text',
                  })}
                </div>
                <div className="md:col-start-4 md:row-span-2">
                  <label className="text-muted-foreground mb-1 block text-sm font-medium">
                    Availability Notes
                  </label>
                  <textarea
                    value={formData.availabilityNotes || ''}
                    onChange={(e) =>
                      handleChange('availabilityNotes', e.target.value)
                    }
                    className="border-input bg-background min-h-[120px] w-full resize-y rounded-md border px-3 py-2 text-sm"
                    placeholder="Enter availability notes"
                  />
                </div>
                <div className="md:col-start-2">
                  <label className="text-muted-foreground mb-1 block text-sm font-medium">
                    Location
                  </label>
                  {renderField({
                    key: 'location',
                    label: 'Location',
                    type: 'text',
                  })}
                </div>
                <div>
                  <label className="text-muted-foreground mb-1 block text-sm font-medium">
                    Time Zone
                  </label>
                  {renderField({
                    key: 'timeZone',
                    label: 'Time Zone',
                    type: 'text',
                  })}
                </div>
              </div>
            </div>

            {/* Profiles Section */}
            <div>
              <SectionDivider title="Profiles" />
              <div className="grid grid-cols-1 gap-4 pt-2 md:grid-cols-3">
                {renderFields(PROFILES_FIELDS)}
              </div>
            </div>

            {/* Additional Info Section */}
            <div>
              <SectionDivider title="Additional Info" />
              <div className="grid grid-cols-1 gap-4 pt-2 md:grid-cols-3">
                {renderFields(ADDITIONAL_INFO_FIELDS)}
              </div>
            </div>

            {/* Integrations Section */}
            <div>
              <SectionDivider title="Integrations" />
              <div className="space-y-4 pt-2">
                {/* Notion Database ID */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label className="text-muted-foreground mb-1 block text-sm font-medium">
                      Notion Database ID
                    </label>
                    <div className="relative">
                      <Input
                        type="text"
                        value={formData.notionDatabaseId || ''}
                        onChange={(e) => {
                          const extracted = extractNotionDatabaseId(
                            e.target.value,
                          )
                          handleChange('notionDatabaseId', extracted)
                        }}
                        placeholder="Paste Notion URL or database ID"
                        className={
                          formData.notionDatabaseId &&
                          isValidNotionDatabaseId(formData.notionDatabaseId)
                            ? 'border-green-500 pr-10 focus:border-green-500'
                            : ''
                        }
                      />
                      {formData.notionDatabaseId &&
                        isValidNotionDatabaseId(formData.notionDatabaseId) && (
                          <CheckCircle
                            size={18}
                            className="absolute top-1/2 right-3 -translate-y-1/2 text-green-500"
                          />
                        )}
                    </div>
                  </div>
                  <div className="flex items-end md:col-span-2">
                    <p className="text-muted-foreground pb-2 text-xs">
                      Paste a full Notion URL or just the 32-character database
                      ID. The ID will be extracted automatically.
                    </p>
                  </div>
                </div>

                {/* Notion Templates */}
                <div>
                  <label className="text-muted-foreground mb-2 block text-sm font-medium">
                    Notion Templates (by Type + Source)
                  </label>
                  <div className="space-y-2">
                    {(formData.notionTemplates || []).map((template, index) => (
                      <div
                        // eslint-disable-next-line react/no-array-index-key -- Index needed: new templates share same templateId/source fallbacks until saved
                        key={`${template.templateId || 'new'}-${template.source || 'default'}-${index}`}
                        className="border-border bg-muted/30 flex items-center gap-2 rounded-lg border p-2"
                      >
                        <Input
                          type="text"
                          value={template.name}
                          onChange={(e) =>
                            handleUpdateTemplate(index, 'name', e.target.value)
                          }
                          placeholder="Template name"
                          className="flex-1"
                        />
                        <div className="relative flex-1">
                          <Input
                            type="text"
                            value={template.templateId}
                            onChange={(e) => {
                              const extracted = extractNotionDatabaseId(
                                e.target.value,
                              )
                              handleUpdateTemplate(
                                index,
                                'templateId',
                                extracted,
                              )
                            }}
                            placeholder="Template ID or URL"
                            className={
                              template.templateId &&
                              isValidNotionDatabaseId(template.templateId)
                                ? 'border-green-500 pr-8 focus:border-green-500'
                                : ''
                            }
                          />
                          {template.templateId &&
                            isValidNotionDatabaseId(template.templateId) && (
                              <CheckCircle
                                size={16}
                                className="absolute top-1/2 right-2 -translate-y-1/2 text-green-500"
                              />
                            )}
                        </div>
                        <select
                          value={template.homeworkType || ''}
                          onChange={(e) =>
                            handleUpdateTemplate(
                              index,
                              'homeworkType',
                              e.target.value || null,
                            )
                          }
                          className="border-input bg-background h-9 w-32 rounded-md border px-2 text-sm"
                        >
                          <option value="" disabled>
                            Type...
                          </option>
                          {HOMEWORK_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <select
                          value={template.source ?? ''}
                          onChange={(e) =>
                            handleUpdateTemplate(
                              index,
                              'source',
                              e.target.value || null,
                            )
                          }
                          className="border-input bg-background h-9 w-32 rounded-md border px-2 text-sm"
                        >
                          <option value="" disabled>
                            Source...
                          </option>
                          {(sourcesQuery.data || []).map((src) => (
                            <option key={src.id} value={src.name ?? ''}>
                              {src.name ?? 'Unknown Source'}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => handleRemoveTemplate(index)}
                          className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive rounded p-1.5 transition-colors"
                          title="Remove template"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleAddTemplate}
                      className="text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1.5 rounded-lg border border-dashed border-current px-3 py-2 text-sm transition-colors"
                    >
                      <Plus size={14} />
                      Add Template
                    </button>
                  </div>
                  <p className="text-muted-foreground mt-2 text-xs">
                    Map homework type + source combinations to Notion templates.
                    When pushing homework, the matching template will be used
                    automatically.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
